import { useState, useEffect } from "react";
import { revenueTiers } from "./pricingData";

const HOURLY_RATE = 65; // assumed hourly cost of manual bookkeeping (AUD)

export default function SavingsCalculator({ onSavingsChange, revenueTier = 1 }) {
  const [inputs, setInputs] = useState({
    currentSpend: "",
    hoursPerWeek: "",
    employees: "",
    softwareCosts: "",
  });

  const update = (field, value) =>
    setInputs((prev) => ({ ...prev, [field]: value }));

  // Defaults used for slider savings when user hasn't entered anything
  const DEFAULT_SPEND = 2000;
  const DEFAULT_HOURS = 10;
  const DEFAULT_SOFTWARE = 200;

  const hasInput =
    inputs.currentSpend !== "" ||
    inputs.hoursPerWeek !== "" ||
    inputs.softwareCosts !== "";

  const currentSpend = hasInput ? parseFloat(inputs.currentSpend) || 0 : DEFAULT_SPEND;
  const hoursPerWeek = hasInput ? parseFloat(inputs.hoursPerWeek) || 0 : DEFAULT_HOURS;
  const softwareCosts = hasInput ? parseFloat(inputs.softwareCosts) || 0 : DEFAULT_SOFTWARE;
  const tier = revenueTiers[revenueTier];
  const ledgerowlPrice = tier.complete || 899; // fallback for custom tier

  const laborCost = hoursPerWeek * HOURLY_RATE * 4;
  const totalCurrentCost = currentSpend + laborCost + softwareCosts;
  const monthlySavings = Math.max(0, totalCurrentCost - ledgerowlPrice);
  const annualSavings = monthlySavings * 12;
  const hoursSaved = hoursPerWeek * 4;

  useEffect(() => {
    if (onSavingsChange) {
      onSavingsChange(annualSavings);
    }
  }, [annualSavings, onSavingsChange]);

  return (
    <section className="py-[100px] px-6 lg:px-10 bg-cream">
      <div className="max-w-[900px] mx-auto">
        <div className="text-center mb-12 fade-in">
          <h2 className="font-inter text-[24px] sm:text-[32px] lg:text-[38px] font-black tracking-[-1.5px] leading-[1.15] mb-4">
            Calculate Your Savings
          </h2>
          <p className="font-inter text-base text-gray-cool-500 leading-[1.7] max-w-[600px] mx-auto">
            See how much you could save by switching to Ledgerowl. Enter your
            current costs below.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-10 fade-in">
          {/* Inputs */}
          <div className="bg-base-white rounded-[20px] p-8 sm:p-10 border border-gray-cool-200">
            <div className="space-y-6">
              <div>
                <label className="block font-inter text-[13px] font-semibold text-gray-cool-700 mb-2">
                  Current monthly bookkeeping spend (AUD)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-cool-400 font-medium">
                    $
                  </span>
                  <input
                    type="number"
                    placeholder="2,000"
                    value={inputs.currentSpend}
                    onChange={(e) => update("currentSpend", e.target.value)}
                    className="w-full pl-8 pr-4 py-3 rounded-xl border border-gray-cool-200 font-inter text-[14px] text-gray-cool-900 focus:outline-none focus:border-blue-primary-500 focus:ring-1 focus:ring-blue-primary-500 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block font-inter text-[13px] font-semibold text-gray-cool-700 mb-2">
                  Hours spent on bookkeeping per week
                </label>
                <input
                  type="number"
                  placeholder="10"
                  value={inputs.hoursPerWeek}
                  onChange={(e) => update("hoursPerWeek", e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-cool-200 font-inter text-[14px] text-gray-cool-900 focus:outline-none focus:border-blue-primary-500 focus:ring-1 focus:ring-blue-primary-500 transition-colors"
                />
              </div>

              <div>
                <label className="block font-inter text-[13px] font-semibold text-gray-cool-700 mb-2">
                  Number of employees
                </label>
                <input
                  type="number"
                  placeholder="15"
                  value={inputs.employees}
                  onChange={(e) => update("employees", e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-cool-200 font-inter text-[14px] text-gray-cool-900 focus:outline-none focus:border-blue-primary-500 focus:ring-1 focus:ring-blue-primary-500 transition-colors"
                />
              </div>

              <div>
                <label className="block font-inter text-[13px] font-semibold text-gray-cool-700 mb-2">
                  Current software costs per month (AUD)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-cool-400 font-medium">
                    $
                  </span>
                  <input
                    type="number"
                    placeholder="200"
                    value={inputs.softwareCosts}
                    onChange={(e) => update("softwareCosts", e.target.value)}
                    className="w-full pl-8 pr-4 py-3 rounded-xl border border-gray-cool-200 font-inter text-[14px] text-gray-cool-900 focus:outline-none focus:border-blue-primary-500 focus:ring-1 focus:ring-blue-primary-500 transition-colors"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="lg:w-[300px] flex flex-col justify-center">
            {hasInput ? (
              <div className="bg-blue-primary-900 text-white rounded-[20px] p-8 text-center">
                <p className="font-inter text-[11px] font-bold uppercase tracking-[1px] text-white/50 mb-4">
                  Estimated Savings
                </p>
                <div className="mb-6">
                  <p className="font-inter text-[42px] font-black tracking-[-2px] leading-none">
                    ${monthlySavings.toLocaleString()}
                  </p>
                  <p className="font-inter text-[13px] text-white/60 mt-1">
                    per month
                  </p>
                </div>
                <div className="h-px bg-white/10 my-5" />
                <div className="space-y-4">
                  <div>
                    <p className="font-inter text-[24px] font-black tracking-[-1px]">
                      ${annualSavings.toLocaleString()}
                    </p>
                    <p className="font-inter text-[12px] text-white/50">
                      annual savings
                    </p>
                  </div>
                  <div>
                    <p className="font-inter text-[24px] font-black tracking-[-1px]">
                      {hoursSaved}hrs
                    </p>
                    <p className="font-inter text-[12px] text-white/50">
                      saved per month
                    </p>
                  </div>
                </div>
                <a
                  href="/contact"
                  className="mt-6 block w-full text-center py-3 rounded-full bg-blue-primary-500 text-white text-[14px] font-semibold hover:bg-[#005bb5] transition-all duration-300"
                >
                  Get Started
                </a>
              </div>
            ) : (
              <div className="bg-gray-cool-100 rounded-[20px] p-8 text-center">
                <p className="font-inter text-[14px] text-gray-cool-500">
                  Enter your current costs to see how much you could save with
                  Ledgerowl.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
